import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { Plus, Users, BookOpen, Activity, TrendingUp, Copy, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import InternalLayout from './InternalLayout';
import './TeacherDashboard.css';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'platform_admin';
  const isTeacher = user?.role === 'teacher' || isAdmin;
  const userRole = user?.role;
  const userId = user?.id;
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [copiedInviteId, setCopiedInviteId] = useState(null);
  const [teacherInviteForm, setTeacherInviteForm] = useState({
    maxUses: '',
    expiresAt: '',
    notes: '',
    recipientEmail: ''
  });
  const [studentInviteForm, setStudentInviteForm] = useState({
    classId: '',
    maxUses: '',
    expiresAt: '',
    notes: '',
    recipientEmail: ''
  });
  const [stats, setStats] = useState({
    totalClasses: 0,
    totalStudents: 0,
    totalSessions: 0,
    activeToday: 0
  });

  useEffect(() => {
    if (!userId && !userRole) {
      return;
    }
    fetchClasses();
    if (isTeacher) {
      fetchInvites();
    }
  }, [userId, userRole, isTeacher]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/classes/', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setClasses(response.data);

      if (response.data.length) {
        setStudentInviteForm((prev) => ({
          ...prev,
          classId: prev.classId || response.data[0].id
        }));
      }
      
      // Calculate stats
      const totalStudents = response.data.reduce((sum, cls) => sum + (cls.student_count || 0), 0);
      const totalSessions = response.data.reduce((sum, cls) => sum + (cls.total_sessions || 0), 0);
      
      setStats({
        totalClasses: response.data.length,
        totalStudents,
        totalSessions,
        activeToday: response.data.filter(cls => cls.is_active).length
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/invites/mine', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInvites(response.data || []);
      setInviteLoading(false);
    } catch (error) {
      console.error('Error fetching invites:', error);
      setInviteError('Unable to load invite codes');
      setInviteLoading(false);
    }
  };

  const buildInvitePayload = (form) => {
    const payload = {};
    if (form.maxUses) {
      payload.max_uses = Number(form.maxUses);
    }
    if (form.expiresAt) {
      payload.expires_at = new Date(`${form.expiresAt}T23:59:59Z`).toISOString();
    }
    if (form.notes?.trim()) {
      payload.notes = form.notes.trim();
    }
    if (form.recipientEmail?.trim()) {
      payload.recipient_email = form.recipientEmail.trim();
    }
    return payload;
  };

  const handleCreateTeacherInvite = async (event) => {
    event?.preventDefault?.();
    setInviteError('');
    setInviteSuccess('');
    try {
      const token = localStorage.getItem('access_token');
      const payload = buildInvitePayload(teacherInviteForm);
      const response = await axios.post('/api/invites/teachers', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInvites((prev) => [response.data, ...prev]);
      setTeacherInviteForm({ maxUses: '', expiresAt: '', notes: '', recipientEmail: '' });
      setInviteSuccess('Teacher invite created');
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Failed to create teacher invite');
    }
  };

  const handleCreateStudentInvite = async (event) => {
    event?.preventDefault?.();
    setInviteError('');
    setInviteSuccess('');
    if (!studentInviteForm.classId) {
      setInviteError('Select a class for student invite');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const payload = buildInvitePayload(studentInviteForm);
      payload.class_id = Number(studentInviteForm.classId);
      const response = await axios.post('/api/invites/students', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInvites((prev) => [response.data, ...prev]);
      setStudentInviteForm((prev) => ({
        ...prev,
        maxUses: '',
        expiresAt: '',
        notes: '',
        recipientEmail: ''
      }));
      setInviteSuccess('Student invite created');
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Failed to create student invite');
    }
  };

  const handleToggleInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `/api/invites/${inviteId}/toggle`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setInvites((prev) => prev.map((invite) => (
        invite.id === inviteId ? response.data : invite
      )));
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Unable to update invite');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    setInviteError('');
    setInviteSuccess('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/invites/${inviteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
      setInviteSuccess('Invite deleted');
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Unable to delete invite');
    }
  };

  const handleCopyInvite = (code, inviteId) => {
    navigator.clipboard.writeText(code);
    setCopiedInviteId(inviteId);
    setTimeout(() => setCopiedInviteId(null), 1500);
  };

  const getClassLabel = (classId) => {
    if (!classId) {
      return '—';
    }
    const match = classes.find((cls) => cls.id === classId);
    return match ? match.name : `Class ${classId}`;
  };

  const formatExpiry = (expiresAt) => {
    if (!expiresAt) {
      return 'No expiry';
    }
    return new Date(expiresAt).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Spin tip="Loading dashboard...">
          <div className="loading-panel" />
        </Spin>
      </div>
    );
  }

  return (
    <InternalLayout
      title="Teacher Dashboard"
      description={`Welcome back, ${user?.full_name || user?.username || 'Teacher'}!`}
      actions={(
        <Button type="primary" icon={<Plus size={20} />} onClick={() => setShowCreateModal(true)}>
          <span>Create Class</span>
        </Button>
      )}
    >
    <div className="dashboard-container">

      {/* Stats Cards */}
      <Row className="stats-grid" gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-card">
            <Statistic title="Total Classes" value={stats.totalClasses} prefix={<BookOpen size={20} />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-card">
            <Statistic title="Total Students" value={stats.totalStudents} prefix={<Users size={20} />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-card">
            <Statistic title="Total Sessions" value={stats.totalSessions} prefix={<Activity size={20} />} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card className="stat-card">
            <Statistic title="Active Classes" value={stats.activeToday} prefix={<TrendingUp size={20} />} />
          </Card>
        </Col>
      </Row>

      {/* Classes List */}
      <div className="classes-section">
        <h2>{isAdmin ? 'Organization Classes' : 'My Classes'}</h2>
        
        {classes.length === 0 ? (
          <div className="empty-state">
            <Empty description="No classes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            <Button type="primary" icon={<Plus size={20} />} onClick={() => setShowCreateModal(true)}>
              <span>Create Class</span>
            </Button>
          </div>
        ) : (
          <Row className="classes-grid" gutter={[16, 16]}>
            {classes.map((cls) => (
              <Col xs={24} md={12} xl={8} key={cls.id}>
                <ClassCard
                  classData={cls}
                  isAdmin={isAdmin}
                  onUpdate={fetchClasses}
                />
              </Col>
            ))}
          </Row>
        )}
      </div>

      {isTeacher && (
        <div className="invites-section">
          <div className="invites-header">
            <div>
              <h2>Invite Codes</h2>
              <p>Create teacher or student invites with limits and expiration.</p>
            </div>
          </div>

          <Row className="invites-grid" gutter={[16, 16]}>
            {isAdmin && (
              <Col xs={24} xl={12}>
                <Card className="invite-card" title="Teacher Invites">
                  <p>Admins generate teacher invite codes for onboarding.</p>
                  <Form className="invite-form" layout="vertical" onFinish={handleCreateTeacherInvite}>
                    <Row gutter={[12, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item label="Max Uses">
                          <Input
                            type="number"
                            min="1"
                            value={teacherInviteForm.maxUses}
                            onChange={(event) => setTeacherInviteForm({
                              ...teacherInviteForm,
                              maxUses: event.target.value
                            })}
                            placeholder="Unlimited"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Expires On">
                          <Input
                            type="date"
                            value={teacherInviteForm.expiresAt}
                            onChange={(event) => setTeacherInviteForm({
                              ...teacherInviteForm,
                              expiresAt: event.target.value
                            })}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item label="Send To (Email)">
                          <Input
                            type="email"
                            value={teacherInviteForm.recipientEmail}
                            onChange={(event) => setTeacherInviteForm({
                              ...teacherInviteForm,
                              recipientEmail: event.target.value
                            })}
                            placeholder="Optional recipient email"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item label="Notes">
                          <Input
                            type="text"
                            value={teacherInviteForm.notes}
                            onChange={(event) => setTeacherInviteForm({
                              ...teacherInviteForm,
                              notes: event.target.value
                            })}
                            placeholder="Optional notes"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Button type="primary" htmlType="submit">Create Teacher Invite</Button>
                  </Form>
                </Card>
              </Col>
            )}

            <Col xs={24} xl={isAdmin ? 12 : 24}>
              <Card className="invite-card" title="Student Invites">
                <p>Generate student invites tied to a class join.</p>
                <Form className="invite-form" layout="vertical" onFinish={handleCreateStudentInvite}>
                  <Row gutter={[12, 0]}>
                    <Col xs={24} md={12}>
                      <Form.Item label="Class">
                        <Select
                          value={studentInviteForm.classId}
                          onChange={(value) => setStudentInviteForm({
                            ...studentInviteForm,
                            classId: value
                          })}
                          options={[
                            { value: '', label: 'Select class' },
                            ...classes.map((cls) => ({ value: cls.id, label: cls.name })),
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Max Uses">
                        <Input
                          type="number"
                          min="1"
                          value={studentInviteForm.maxUses}
                          onChange={(event) => setStudentInviteForm({
                            ...studentInviteForm,
                            maxUses: event.target.value
                          })}
                          placeholder="Unlimited"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Send To (Email)">
                        <Input
                          type="email"
                          value={studentInviteForm.recipientEmail}
                          onChange={(event) => setStudentInviteForm({
                            ...studentInviteForm,
                            recipientEmail: event.target.value
                          })}
                          placeholder="Optional recipient email"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Expires On">
                        <Input
                          type="date"
                          value={studentInviteForm.expiresAt}
                          onChange={(event) => setStudentInviteForm({
                            ...studentInviteForm,
                            expiresAt: event.target.value
                          })}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item label="Notes">
                        <Input
                          type="text"
                          value={studentInviteForm.notes}
                          onChange={(event) => setStudentInviteForm({
                            ...studentInviteForm,
                            notes: event.target.value
                          })}
                          placeholder="Optional notes"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button type="primary" htmlType="submit">Create Student Invite</Button>
                </Form>
              </Card>
            </Col>
          </Row>

          {inviteError && <Alert className="invite-message" type="error" showIcon message={inviteError} />}
          {inviteSuccess && <Alert className="invite-message" type="success" showIcon message={inviteSuccess} />}

          <div className="invite-list">
            {inviteLoading ? (
              <Spin tip="Loading invites...">
                <div className="loading-panel" />
              </Spin>
            ) : invites.length === 0 ? (
              <div className="empty-state small">
                <Empty description="No invites yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : (
              <Table
                className="invite-table"
                rowKey="id"
                dataSource={invites}
                pagination={false}
                scroll={{ x: 860 }}
                rowClassName={(invite) => {
                  const isExpired = invite.expires_at && new Date(invite.expires_at) < new Date();
                  return !invite.is_active || isExpired ? 'inactive' : '';
                }}
                columns={[
                  {
                    title: 'Code',
                    dataIndex: 'code',
                    key: 'code',
                    render: (code, invite) => (
                      <div className="invite-code">
                        <code>{code}</code>
                        <Button
                          type="text"
                          icon={copiedInviteId === invite.id ? null : <Copy size={16} />}
                          htmlType="button"
                          onClick={() => handleCopyInvite(code, invite.id)}
                          title="Copy invite code"
                        >
                          {copiedInviteId === invite.id ? 'Copied' : null}
                        </Button>
                      </div>
                    )
                  },
                  {
                    title: 'Role',
                    dataIndex: 'role',
                    key: 'role',
                    render: (role) => <Tag className="invite-role">{role.toUpperCase()}</Tag>
                  },
                  {
                    title: 'Class',
                    dataIndex: 'class_id',
                    key: 'class',
                    render: (classId) => getClassLabel(classId)
                  },
                  {
                    title: 'Uses',
                    key: 'uses',
                    render: (_, invite) => `${invite.uses}/${invite.max_uses ?? '∞'}`
                  },
                  {
                    title: 'Expires',
                    dataIndex: 'expires_at',
                    key: 'expires',
                    render: (expiresAt) => formatExpiry(expiresAt)
                  },
                  {
                    title: 'Status',
                    key: 'status',
                    render: (_, invite) => {
                      const isExpired = invite.expires_at && new Date(invite.expires_at) < new Date();
                      const statusLabel = !invite.is_active
                        ? 'Disabled'
                        : isExpired
                          ? 'Expired'
                          : 'Active';
                      return <Tag className={`invite-status ${statusLabel.toLowerCase()}`}>{statusLabel}</Tag>;
                    }
                  },
                  {
                    title: 'Actions',
                    key: 'actions',
                    align: 'right',
                    render: (_, invite) => (
                      <div className="invite-actions">
                        <Button onClick={() => handleToggleInvite(invite.id)}>
                          {invite.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Popconfirm
                          title="Delete this invite code?"
                          description="This cannot be undone."
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDeleteInvite(invite.id)}
                        >
                          <Button danger>Delete</Button>
                        </Popconfirm>
                      </div>
                    )
                  }
                ]}
              />
            )}
          </div>
        </div>
      )}

      {/* Create Class Modal */}
      {showCreateModal && (
        <CreateClassModal 
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchClasses();
          }}
        />
      )}
    </div>
    </InternalLayout>
  );
};

const ClassCard = ({ classData, isAdmin, onUpdate }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/classes/${classData.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onUpdate();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete class');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      className="class-card"
      title={classData.name}
      extra={<Tag color={classData.is_active ? 'success' : 'default'}>{classData.is_active ? 'Active' : 'Inactive'}</Tag>}
    >
      {classData.description && <p className="class-description">{classData.description}</p>}

      {isAdmin && (classData.teacher_name || classData.teacher_email) && (
        <div className="class-teacher">
          Teacher: {classData.teacher_name || classData.teacher_email}
        </div>
      )}

      <Space className="class-stats" size={[16, 8]} wrap>
        <span className="class-stat-item">
          <Users size={16} />
          <span>{classData.student_count || 0} students</span>
        </span>
        <span className="class-stat-item">
          <Activity size={16} />
          <span>{classData.total_sessions || 0} sessions</span>
        </span>
      </Space>

      <div className="class-join-code">
        <label>Join Code:</label>
        <code className="join-code">{classData.join_code}</code>
      </div>

      <Space className="class-card-actions" wrap>
        <Link to={`/teaching/classes/${classData.id}`}>
          <Button>
            View Details
          </Button>
        </Link>
        <Popconfirm
          title={`Delete class "${classData.name}"?`}
          description="This will deactivate the class."
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={handleDelete}
        >
          <Button danger icon={<Trash2 size={16} />} loading={deleting} title="Delete Class">
            Delete
          </Button>
        </Popconfirm>
      </Space>
    </Card>
  );
};

const CreateClassModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Class name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        '/api/classes/',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create class');
      setLoading(false);
    }
  };

  return (
    <Modal open onCancel={onClose} footer={null} centered title="Create New Class">
      <div className="modal-content antd-modal-content">
        <Form onSubmitCapture={handleSubmit} layout="vertical">
          <div className="form-group">
            <label>Class Name *</label>
            <Input
              type="text"
              placeholder="e.g., Physics 101, Grade 5 Math"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>Description (Optional)</label>
            <Input.TextArea
              placeholder="Brief description of the class"
              rows={3}
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          {error && <Alert className="error-message" type="error" showIcon message={error} />}

          <div className="modal-actions">
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {loading ? 'Creating...' : 'Create Class'}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default TeacherDashboard;
